/**
 * SaveToPhoneModal — the user-facing "Save to My Phone" surface.
 *
 * Shows one of two faces depending on the device:
 *   • Android (Chrome / Edge / Samsung Internet) → "Save to My Phone" button
 *     that invokes the native beforeinstallprompt flow.
 *   • iOS Safari → 3-step Share / Add to Home Screen / Add instructions.
 *
 * Appears when `promptKind` is set by the PwaInstallProvider — either
 * automatically after a meaningful interaction, or manually from a menu
 * entry. Dismissible per visit; non-blocking modal (click outside, Esc,
 * explicit "Not now" button).
 */
import React, { useEffect } from "react";
import { usePwaInstall } from "../context/PwaInstallContext";
import { Smartphone, Share2, Plus, X, Check, Download, ArrowRight } from "lucide-react";

export default function SaveToPhoneModal() {
  const { promptKind, promptTrigger, promptInstall, dismissPrompt } = usePwaInstall();

  useEffect(() => {
    // Fire ios-instructions-shown exactly when the iOS face mounts for the
    // first time within this trigger. (Manual opens also fire from context.)
    if (promptKind === "ios" && promptTrigger !== "manual") {
      window.dispatchEvent(new CustomEvent("pwa:ios-instructions-shown", {
        detail: { trigger: promptTrigger },
      }));
    }
  }, [promptKind, promptTrigger]);

  useEffect(() => {
    if (!promptKind) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismissPrompt(0);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [promptKind, dismissPrompt]);

  if (!promptKind) return null;

  const isIos = promptKind === "ios";

  return (
    <div
      data-testid="pwa-save-modal"
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-4 pb-4 sm:pb-0"
      onClick={() => dismissPrompt(0)}
    >
      <div
        role="dialog"
        aria-label="Save LifeGuide to your phone"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-3xl bg-[#F5F0E5] text-[#2E2E2E] shadow-2xl p-6 sm:p-7 fade-in-up"
      >
        <button
          type="button"
          data-testid="pwa-save-close"
          onClick={() => dismissPrompt(0)}
          aria-label="Close"
          className="absolute right-3 top-3 w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center text-[#6B6B6B]"
        >
          <X size={18} />
        </button>

        <div className="w-14 h-14 rounded-2xl bg-[#4F6F52] text-white flex items-center justify-center shadow-[0_6px_18px_rgba(79,111,82,0.3)] mb-4">
          <Smartphone size={26} strokeWidth={2.1} />
        </div>

        <h2 className="font-display text-2xl leading-snug mb-1" data-testid="pwa-save-title">
          Save LifeGuide to your phone
        </h2>
        <p className="text-sm text-[#5F5F5F] leading-relaxed mb-5">
          Keep LifeGuide one tap away. Opens like an app — no App Store, no account changes.
        </p>

        {isIos ? (
          <ol className="space-y-3 mb-6" data-testid="pwa-ios-steps">
            <IosStep n={1} icon={<Share2 size={16} />} text={<>Tap the <strong>Share</strong> button at the bottom of Safari.</>} />
            <IosStep n={2} icon={<Plus size={16} />}   text={<>Scroll and tap <strong>Add to Home Screen</strong>.</>} />
            <IosStep n={3} icon={<Check size={16} />}  text={<>Tap <strong>Add</strong> in the top-right corner. Done.</>} />
          </ol>
        ) : (
          <div className="mb-6">
            <button
              type="button"
              data-testid="pwa-save-cta"
              onClick={() => promptInstall()}
              className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-3 font-medium transition shadow-[0_6px_18px_rgba(79,111,82,0.28)] inline-flex items-center justify-center gap-2"
            >
              <Download size={16} /> Save to My Phone
              <ArrowRight size={14} className="opacity-80" />
            </button>
            <p className="text-xs text-[#6B6B6B] mt-3 text-center">
              Your phone will ask to confirm. Takes 2 seconds.
            </p>
          </div>
        )}

        <button
          type="button"
          data-testid="pwa-save-dismiss"
          onClick={() => dismissPrompt(0)}
          className="text-sm text-[#6B6B6B] hover:text-[#2E2E2E] underline underline-offset-2"
        >
          Not now
        </button>
        <button
          type="button"
          data-testid="pwa-save-dismiss-week"
          onClick={() => dismissPrompt(7)}
          className="ml-4 text-xs text-[#8C8C8C] hover:text-[#2E2E2E]"
        >
          Don't ask for a week
        </button>
      </div>
    </div>
  );
}

function IosStep({ n, icon, text }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-7 h-7 rounded-full bg-[#4F6F52] text-white text-xs font-semibold flex items-center justify-center">
        {n}
      </span>
      <span className="flex items-center gap-1.5 text-[#2E2E2E] text-sm leading-relaxed">
        <span className="text-[#4F6F52]">{icon}</span>
        {text}
      </span>
    </li>
  );
}
